import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #isSyncing = false;

  /** @type {HTMLElement | null} */
  #headerCartIcon = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDrawerClose);

    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:remove', this.#handleCartRemove);

    // Sync at multiple intervals to survive cart-icon's own initialization
    // which resets the bubble back to visually-hidden after we set it.
    // 100ms  - early attempt
    // 500ms  - after most components initialize
    // 1500ms - after lazy/deferred components
    // 3000ms - final safety net
    [100, 500, 1500, 3000].forEach(delay => {
      setTimeout(() => {
        this.#findHeaderCartElements();
        this.#syncCartCount();
      }, delay);
    });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDrawerClose);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:remove', this.#handleCartRemove);
    this.#historyAbortController?.abort();
  }

  /**
   * Finds and caches the cart-icon custom element.
   */
  #findHeaderCartElements() {
    this.#headerCartIcon = document.querySelector('cart-icon');
  }

  /**
   * Ensures the missing cartBubbleCount ref span exists inside cart-icon's bubble.
   * Without it, cart-icon's MutationObserver throws MissingRefError on every DOM change.
   * @param {HTMLElement} cartIcon
   */
  #ensureCartIconRefs(cartIcon) {
    const refs = cartIcon.refs;
    if (!refs?.cartBubble) return;

    if (!refs.cartBubble.querySelector('[ref="cartBubbleCount"]')) {
      const span = document.createElement('span');
      span.setAttribute('ref', 'cartBubbleCount');
      span.className = 'cart-bubble__text-count hidden';
      refs.cartBubble.appendChild(span);
    }
  }

  /**
   * Applies or removes visible styles on the cart bubble.
   * visually-hidden uses clip:rect(0,0,0,0) + zero width/height to hide.
   * We override all properties with !important inline styles.
   * @param {HTMLElement} bubble
   * @param {boolean} visible
   */
  #setBubbleVisible(bubble, visible) {
    if (visible) {
      bubble.classList.remove('visually-hidden');
      bubble.style.setProperty('clip', 'auto', 'important');
      bubble.style.setProperty('clip-path', 'none', 'important');
      bubble.style.setProperty('width', 'auto', 'important');
      bubble.style.setProperty('height', 'auto', 'important');
      bubble.style.setProperty('overflow', 'visible', 'important');
      bubble.style.setProperty('position', 'absolute', 'important');
      bubble.style.setProperty('white-space', 'nowrap', 'important');
    } else {
      bubble.style.removeProperty('clip');
      bubble.style.removeProperty('clip-path');
      bubble.style.removeProperty('width');
      bubble.style.removeProperty('height');
      bubble.style.removeProperty('overflow');
      bubble.style.removeProperty('position');
      bubble.style.removeProperty('white-space');
      bubble.classList.add('visually-hidden');
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #onDrawerOpen = async () => {
    await this.#syncCartCount();
    await this.#refreshCartDrawer();
  };

  #onDrawerClose = async () => {
    await this.#syncCartCount();
  };

  /**
   * Fetches real cart count from Shopify and updates the header bubble.
   */
  async #syncCartCount() {
    if (this.#isSyncing) return;

    try {
      this.#isSyncing = true;

      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Cart fetch failed');

      const cart = await response.json();
      const itemCount = cart.item_count || 0;

      this.#updateHeaderCartCount(itemCount);

      window.dispatchEvent(new CustomEvent('cart:count:updated', {
        detail: { count: itemCount, cart }
      }));

    } catch (error) {
      console.error('Failed to sync cart count:', error);
    } finally {
      this.#isSyncing = false;
    }
  }

  /**
   * Updates the cart-icon bubble count and visibility.
   * @param {number} count
   */
  #updateHeaderCartCount(count) {
    const itemCount = count !== undefined ? parseInt(count, 10) : 0;
    if (isNaN(itemCount)) return;

    const cartIcon = this.#headerCartIcon || document.querySelector('cart-icon');
    if (!cartIcon) return;
    if (!this.#headerCartIcon) this.#headerCartIcon = cartIcon;

    // Inject missing ref before touching DOM
    this.#ensureCartIconRefs(cartIcon);

    const refs = cartIcon.refs;
    if (!refs?.cartBubbleText || !refs?.cartBubble) return;

    // Update count text
    refs.cartBubbleText.textContent = itemCount > 0 ? itemCount.toString() : '';

    // Apply visibility immediately
    this.#setBubbleVisible(refs.cartBubble, itemCount > 0);

    // Re-apply on next frame to survive post-render resets by cart-icon
    if (itemCount > 0) {
      requestAnimationFrame(() => {
        this.#setBubbleVisible(refs.cartBubble, true);
      });
    }
  }

  /**
   * Handles cart add events.
   */
  #handleCartAdd = (event) => {
    console.log('Cart add event detected');

    const itemCount = event.detail.resource?.item_count;

    if (itemCount !== undefined) {
      this.#updateHeaderCartCount(itemCount);
    }

    this.#syncCartCount();
    this.#announceCartCount(itemCount);

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      setTimeout(() => {
        this.#refreshCartDrawer();
      }, 300);
    }
  };

  /**
   * Handles cart update events.
   */
  #handleCartUpdate = async (event) => {
    console.log('Cart update event detected');
    await this.#syncCartCount();

    if (this.refs.dialog?.open) {
      this.#refreshCartDrawer();
    }
  };

  /**
   * Handles cart remove events.
   */
  #handleCartRemove = async (event) => {
    console.log('Cart remove event detected');
    await this.#syncCartCount();

    if (this.refs.dialog?.open) {
      this.#refreshCartDrawer();
    }
  };

  /**
   * Refreshes the cart drawer content via Shopify sections API.
   */
  async #refreshCartDrawer() {
    try {
      const cartItemsComponent = this.querySelector('cart-items-component, .cart-drawer__items, .cart-items, [data-section-id]');
      let sectionId = cartItemsComponent?.dataset?.sectionId || 'cart-drawer';

      const response = await fetch(`${window.Shopify.routes.root}?sections=${sectionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store'
      });

      const data = await response.json();

      if (!data[sectionId]) {
        const fallbacks = ['main-cart-items', 'cart-template', 'cart', 'main-cart'];
        for (const fallback of fallbacks) {
          if (data[fallback]) {
            sectionId = fallback;
            break;
          }
        }
      }

      const html = data[sectionId];
      if (!html) {
        console.warn('No HTML found for cart section:', sectionId);
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newCartItems = doc.querySelector('cart-items-component, .cart-drawer__items, .cart-items');
      const currentCartItems = this.querySelector('cart-items-component, .cart-drawer__items, .cart-items');

      if (newCartItems && currentCartItems) {
        currentCartItems.innerHTML = newCartItems.innerHTML;

        if (window.customElements) {
          currentCartItems.querySelectorAll('*').forEach(el => {
            const tagName = el.tagName.toLowerCase();
            if (tagName.includes('-') && customElements.get(tagName)) {
              if (!el.hasAttribute('data-initialized')) {
                el.setAttribute('data-initialized', 'true');
              }
            }
          });
        }
      }

      this.#syncCartCount();

    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }

  /**
   * Announces cart count to screen readers.
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}